import { useEffect, ReactNode, useCallback } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { AMPLITUDE_API_KEY, NODE_ENV } from 'src/config';
import {
  AmplitudeUserProperties,
  EventNamesMap,
  LogEventFunction,
} from './AmplitudeProvider.types';
import { AmplitudeContext } from 'src/context/AmplitudeProvider/useAmplitude';

import useAppVersion from 'src/common/hooks/useAppVersion';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import * as Sentry from '@sentry/react';
import { trackEvent as trackFirebaseEvent } from 'src/firebase-config';
import { AnalyticsEventMapper } from 'src/context/AmplitudeProvider/AmplitudeAnalyticsEventMapper';
import useLocalStorageState from 'src/common/hooks/useLocalStorageState';
import { LocalStorageKeys } from 'src/types/common';
import { FacebookEvents } from 'capacitor-facebook-events';
import { Capacitor } from '@capacitor/core';

export const AmplitudeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { appVersion } = useAppVersion();

  const [storedSessionId, setStoredSessionId] = useLocalStorageState(
    LocalStorageKeys.AmplitudeSessionId,
    undefined
  );

  useEffect(() => {
    amplitude.init(AMPLITUDE_API_KEY, {
      serverZone: 'US',
      defaultTracking: {
        pageViews: true,
        sessions: true,
        formInteractions: true,
        fileDownloads: true,
      },
      logLevel: amplitude.Types.LogLevel.None,
    });

    if (NODE_ENV === 'production') {
      const sessionReplayTracking = sessionReplayPlugin({
        forceSessionTracking: false,
        sampleRate: 1,
      });

      amplitude.add(sessionReplayTracking);
    }
  }, []);

  const logEvent: LogEventFunction = useCallback(
    (eventName, data) => {
      const isWeb = !Capacitor.isNativePlatform();

      amplitude
        .track(eventName, {
          ...data,
          app_version: appVersion,
          is_web: isWeb,
        })
        .promise.then((result) => result)
        .catch((reason) => Sentry.captureException(reason));

      try {
        const { firebaseEventName, firebaseParams } =
          AnalyticsEventMapper.mapToFirebaseEvent(eventName, data);
        if (firebaseEventName) {
          trackFirebaseEvent(firebaseEventName, firebaseParams);
        }

        const { facebookEventName, facebookParams } =
          AnalyticsEventMapper.mapToFacebookEvent(eventName, data);

        if (facebookEventName) {
          FacebookEvents.logEvent({
            event: facebookEventName,
            params: facebookParams,
          });
        } else {
          FacebookEvents.logEvent({
            event: eventName,
            params: { ...data, app_version: appVersion },
          });
        }
      } catch (e) {
        Sentry.captureException(e);
      }
    },
    [appVersion]
  );

  const setAmplitudeUser = useCallback(
    (userProperties: AmplitudeUserProperties) => {
      const newAmplitudeUser = { ...userProperties, app_version: appVersion };

      amplitude.setUserId(newAmplitudeUser.email);
      const identify = new amplitude.Identify();
      Object.entries(newAmplitudeUser).forEach(([key, value]) => {
        key && value && identify.set(key, value);
      });
      amplitude.identify(identify);

      const currentSessionId = amplitude.getSessionId();
      if (newAmplitudeUser.email && currentSessionId !== storedSessionId) {
        logEvent(EventNamesMap.StartAuthenticatedSession, userProperties);
        setStoredSessionId(currentSessionId);
      }
    },
    [appVersion, logEvent, storedSessionId, setStoredSessionId]
  );

  const resetAmplitudeUser = useCallback(() => {
    amplitude.reset();

    setStoredSessionId(undefined);
  }, [setStoredSessionId]);

  return (
    <AmplitudeContext.Provider
      value={{ logEvent, setAmplitudeUser, resetAmplitudeUser }}
    >
      {children}
    </AmplitudeContext.Provider>
  );
};
